import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ErroRequisicao } from '../../web/src/nucleo/api';
import { ppost } from './api';
import { clienteDoLink, semCliente } from './destino';

/**
 * Review r52 (VIS-M2, SPEC-B2): o link da fatura da mensagem de cobrança leva o cliente (`?cliente=<id>`). Antes de abrir a
 * tela, a sessão vai ao acesso da conta nesse cliente (`POST /sessao/cliente`; a conta com várias empresas e a sessão na outra
 * caía em "não encontrado"); sem esse acesso, a tela avisa. Depois, o endereço fica sem o parâmetro.
 *
 * Review r53: só o 404 `portal.empresa_nao_encontrada` é "Fatura de outra empresa" (ROB-B1 = VIS-B3). Outro erro (rede,
 * 503, 429) mantém o `?cliente=` no endereço, não grava aviso no histórico e devolve o erro para a tela mostrar com
 * "Tentar de novo"; no 401 não navega (o `aoExpirar` já leva à entrada com o destino inteiro). Cada chegada do link
 * (`location.key`) é uma troca: o mesmo cliente de novo (o app nativo navega no mesmo processo) troca de novo, em vez de
 * ficar em "Carregando" (SPEC-B2).
 */
export function useClienteDoLink(pronto: boolean): { trocando: boolean; erro: unknown; tentar: () => void } {
  const local = useLocation();
  const navegar = useNavigate();
  const qc = useQueryClient();
  const cliente = clienteDoLink(local.search);
  const [tentativa, setTentativa] = useState(0);
  const [falha, setFalha] = useState<{ chave: string; erro: unknown } | null>(null);
  const feito = useRef<string | null>(null);
  const chave = `${local.key}:${cliente ?? ''}:${tentativa}`;
  useEffect(() => {
    if (!cliente || !pronto || feito.current === chave) return;
    feito.current = chave;
    const limpo = local.pathname + semCliente(local.search);
    ppost<{ trocou: boolean }>('/sessao/cliente', { parceiro_id: cliente }).then(
      async (r) => {
        // Outra empresa: o que estava em cache era da anterior.
        if (r?.trocou) await qc.resetQueries();
        navegar(limpo, { replace: true });
      },
      (e: unknown) => {
        if (e instanceof ErroRequisicao && e.status === 404 && e.corpo.code === 'portal.empresa_nao_encontrada') {
          navegar(limpo, { replace: true, state: { clienteNaoEncontrado: true } });
          return;
        }
        if (e instanceof ErroRequisicao && e.status === 401) return;
        setFalha({ chave, erro: e });
      },
    );
  }, [cliente, pronto, chave, local.pathname, local.search, navegar, qc]);
  const tentar = useCallback(() => setTentativa((n) => n + 1), []);
  return { trocando: !!cliente, erro: falha?.chave === chave ? falha.erro : null, tentar };
}
